// The Scheduled page's store (busy-my-web#308): the caller's mail still waiting on the clock,
// soonest first, and the one thing that can be done to it — cancelling before the moment.
//
// Both routes already exist on the BFF (busy-main-bff#14) and `email.js` already wraps them for
// the composer's Undo (`fetchScheduledEmails` / `cancelScheduledSend`). This module is the PAGE's
// copy of the list: it keeps the items, the loading flag and the count the sidebar shows, which
// the composer's fire-and-forget helpers never needed.
import api from '@/lib/services/general.service'
import { toast } from 'vue3-toastify'
import { sortScheduled } from '@/lib/emailPageEmpty'
import { composedFromPayload } from '@/lib/scheduledMessage'

export const state = {
  items: [],
  isLoading: false,
  loaded: false,
  // The COMPOSED copy of a message this session scheduled, by schedule id (busy-my-web#442). The
  // list route answers a summary — id, subject, sendAt, recipients — and has no field for the body,
  // so the only place the body of a queued message exists on the client is the payload the box sent.
  // The reader (/emails/scheduled/:id) draws the email from the summary and fills the body from
  // here; a message opened in a later session simply has none, which the page says in words.
  composed: {}
}

export const getters = {
  scheduledEmails: (s) => s.items,
  scheduledCounts: (s) => s.items.length,
  isLoading: (s) => s.isLoading,
  loaded: (s) => s.loaded,
  composedMessage: (s) => (id) => s.composed[String(id ?? '')] || null
}

export const actions = {
  // The box, the moment it parks a mail on the clock: keep what it sent, so "View" can open the
  // message AS AN EMAIL rather than as the summary row the folder lists.
  remember({ commit }, { id, payload } = {}) {
    const composed = composedFromPayload(payload)
    if (!id || !composed) return
    commit('SET_COMPOSED', { id: String(id), composed })
  },
  async fetchEmails({ commit }) {
    commit('SET_LOADING', true)
    try {
      const response = await api.get('email/scheduled', {})
      commit('SET_ITEMS', response?.data ?? [])
    } catch (error) {
      console.error('Error fetching scheduled emails:', error)
    } finally {
      commit('SET_LOADING', false)
    }
  },
  // Cancel one or many. Each is its own DELETE (the BFF has no bulk cancel); a 404 means it has
  // already gone out or been cancelled, which is reported rather than hidden — telling the user a
  // message was pulled back when it was already on its way would be worse than the error.
  async cancel({ commit }, ids) {
    const list = (Array.isArray(ids) ? ids : [ids]).filter(Boolean)
    if (!list.length) return { cancelled: [], failed: [] }
    const toastId = toast.loading('Please wait...', { theme: 'email' })
    const cancelled = []
    const failed = []
    for (const id of list) {
      try {
        const ok = await api.delete('email/scheduled', id)
        if (ok) cancelled.push(id)
        else failed.push(id)
      } catch (error) {
        failed.push(id)
      }
    }
    if (cancelled.length) commit('REMOVE_ITEMS', cancelled)
    const n = cancelled.length
    const message = failed.length
      ? `${failed.length} scheduled ${failed.length === 1 ? 'email' : 'emails'} could not be cancelled`
      : n === 1
        ? 'Scheduled send cancelled'
        : `${n} scheduled sends cancelled`
    setTimeout(() => {
      toast.update(toastId, {
        render: message,
        autoClose: true,
        closeOnClick: true,
        closeButton: true,
        theme: 'email',
        isLoading: false
      })
      toast.done(toastId)
    })
    return { cancelled, failed }
  }
}

export const mutations = {
  SET_ITEMS(s, items) {
    s.items = sortScheduled(items)
    s.loaded = true
  },
  SET_LOADING(s, loading) {
    s.isLoading = !!loading
  },
  REMOVE_ITEMS(s, ids) {
    const gone = new Set(ids)
    s.items = s.items.filter((i) => !gone.has(i.id))
    // A cancelled send is not a message any more — its composed copy goes with it.
    for (const id of gone) delete s.composed[String(id)]
  },
  SET_COMPOSED(s, { id, composed }) {
    s.composed = { ...s.composed, [id]: composed }
  }
}

export default { state, getters, actions, mutations }
